import { Box, makeStyles, Typography } from "@material-ui/core";
import ViewCompactIcon from "@material-ui/icons/ViewCompact";
import clsx from "clsx";
import dayjs from "dayjs";
import { FC, useContext, useEffect, useMemo, useState } from "react";
import { from, Observable, of, skipWhile, Subscription, switchMap } from "rxjs";
import { catchError, map } from "rxjs/operators";

import Loader from "../../../../components/Loader";
import TooltipPopover from "../../../../components/TooltipPopover";
import { Query } from "../../../../types/firebase.types";
import { getMidNightDate } from "../../../../utils/generic";
import { CourtSlot } from "../../types";
import { getDayName } from "../../utils";
import { getSlotsInRange } from "../../utils/queries";
import { CalendarContext } from "./index";

const useStyles = makeStyles((theme) => ({
  calendarCell: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    border: `1px solid ${theme.palette.grey[500]}`,
    marginRight: -1,
    marginBottom: -1,
    fontSize: 26,
    padding: theme.spacing(1),
    position: "relative",
    cursor: "pointer",

    "&:hover": {
      backgroundColor: theme.palette.grey[300],
    },
  },
  disabledCell: {
    opacity: 0.5,
    pointerEvents: "none",
  },
}));

const CalendarGridCell: FC<CalendarGridCellProps> = ({ date, index, onOpenModal }) => {
  const classes = useStyles({});
  const [loading, setLoading] = useState(false);
  const [slot, setSlot] = useState<CourtSlot | undefined>(undefined);

  const { courtId } = useContext(CalendarContext);

  const currentDayDate = useMemo(() => dayjs(date).add(index, "day"), [date, index]);

  const oneSlotQuery = (courtId: string, selectedDayDate: Date) => {
    return getSlotsInRange(courtId, selectedDayDate, selectedDayDate).limit(1);
  };

  const getOneSlotInCurrentDay = (query: Query): Observable<any> => {
    return from(query.get()).pipe(
      skipWhile((snapshot) => {
        return snapshot.empty;
      }),
      switchMap((snapshot) => from(snapshot.docs)),
      map((snapshot) => snapshot.data()),
      catchError((ex: any) => {
        console.log(ex.message);
        return of({});
      })
    );
  };

  const canShowCell = () =>
    currentDayDate.isAfter(getMidNightDate(dayjs().toDate())) ||
    currentDayDate.isSame(getMidNightDate(dayjs().toDate()));

  useEffect(() => {
    let querySubscription: Subscription;

    if (canShowCell()) {
      setLoading(true);
      querySubscription = getOneSlotInCurrentDay(oneSlotQuery(courtId, currentDayDate.toDate())).subscribe({
        next(data) {
          setSlot(data);
          setLoading(false);
        },
      });
    }

    return () => {
      if (querySubscription) querySubscription.unsubscribe();
      setSlot(undefined);
    };
  }, [courtId, currentDayDate]);

  return (
    <Box
      key={`${index}`}
      className={clsx([classes.calendarCell, { [classes.disabledCell]: !canShowCell() }])}
      onClick={() => onOpenModal(currentDayDate.toDate())}
    >
      <Typography>{getDayName(currentDayDate.toDate(), false)}</Typography>
      <Typography>{currentDayDate.format("MMM DD, YYYY")}</Typography>

      {canShowCell() ? (
        <>
          {slot ? (
            <Box position="absolute" right={10} bottom={5}>
              <Loader loading={loading}>
                <TooltipPopover message="Has available slots">
                  <ViewCompactIcon fontSize="small" />
                </TooltipPopover>
              </Loader>
            </Box>
          ) : null}
        </>
      ) : null}
    </Box>
  );
};

interface CalendarGridCellProps {
  date: Date;
  index: number;
  onOpenModal: (date: Date) => void;
}

export default CalendarGridCell;
